import React, { useState, useEffect } from 'react'
import classNames from 'classnames'
import Chart from 'react-apexcharts'
import { useDispatch, useSelector } from 'react-redux'
import moment from 'moment'

import classes from './courses.module.css'
import generalClasses from '../BusinessUserDashboard.module.css'
import { HeaderSearch } from '../../../components/Header/Header'
import { ReactComponent as UserIcon } from '../../../assets/icons/colored-user.svg'
import { Button } from '../../../components/Button/Button'
import PageChanger from '../../../components/PageChanger/PageChanger'
import Empty from '../../../components/Empty/Empty'
import EmptyCourseIcon from '../../../assets/images/empty-course.svg'
import { dashboardSelectors } from '../dashboardSlice'
import notification from '../../../components/Notification/notification'
import Spinner from '../../../components/loaders/Spinner'
import { getBusinessUserCourses, getCourseBeneficiaries } from '../dashboardApi'

const Courses = () => {
    const dispatch = useDispatch()
    const coursesData = useSelector(dashboardSelectors.businessUserCourses).data
    const isFetchingCourses = useSelector(dashboardSelectors.isFetching)
        .businessUserCourses
    const coursesErrorMessage = useSelector(dashboardSelectors.errorMessage)
        .businessUserCourses
    const coursesHasError = useSelector(dashboardSelectors.hasError)
        .businessUserCourses
    const courseBeneficiariesData = useSelector(
        dashboardSelectors.courseBeneficiaries
    ).data
    const isFetchingCourseBeneficiaries = useSelector(
        dashboardSelectors.isFetching
    ).courseBeneficiaries
    const courseBeneficiariesErrorMessage = useSelector(
        dashboardSelectors.errorMessage
    ).courseBeneficiaries
    const courseBeneficiariesHasError = useSelector(dashboardSelectors.hasError)
        .courseBeneficiaries
    const [selectedCourse, setSelectedCourse] = useState(null)
    const [courseSearchText, setCourseSearchText] = useState('')
    const [searchText, setSearchText] = useState({
        course: '',
        beneficiaryCourse: '',
    })
    const [beneficiarySearchParams, setBeneficiarySearchParams] = useState({
        searchText: '',
        currentPage: 1,
    })
    const chartIndicatorColors = ['#00C5CC', '#EB6324']

    useEffect(() => {
        dispatch(getBusinessUserCourses({ search: courseSearchText }))
    }, [courseSearchText])

    useEffect(() => {
        if (selectedCourse) {
            dispatch(
                getCourseBeneficiaries(selectedCourse.courseCode, {
                    search: beneficiarySearchParams.searchText,
                    pageSize: 10,
                    pageNo: beneficiarySearchParams.currentPage,
                })
            )
        }
    }, [selectedCourse, beneficiarySearchParams])

    useEffect(() => {
        if (coursesHasError) {
            notification.failure({
                title: 'Failed to load courses',
                description: coursesErrorMessage,
            })
        }
    }, [coursesHasError])

    useEffect(() => {
        if (courseBeneficiariesHasError) {
            notification.failure({
                title: 'Failed to load courses beneficiaries',
                description: courseBeneficiariesErrorMessage,
            })
        }
    }, [courseBeneficiariesHasError])

    const chartOptions = {
        chart: {
            width: 102,
            type: 'pie',
        },
        legend: {
            show: false,
        },
        hover: {
            filter: {
                type: 'none',
            },
        },
        tooltip: {
            enabled: true,
            enabledOnSeries: undefined,
            shared: true,
            y: {
                formatter: function(value) {
                    return value + '%'
                },
            },
        },
        dataLabels: {
            enabled: true,
        },
        labels: selectedCourse ? ['Ongoing', 'Completed'] : [],
        colors: chartIndicatorColors,
        responsive: [
            {
                breakpoint: 480,
                options: {
                    chart: {
                        width: 200,
                    },
                    legend: {
                        position: 'bottom',
                    },
                },
            },
        ],
    }

    const renderCourseDetails = () => {
        return (
            <div className={classes.courseDetails}>
                <div className="row">
                    <div className="col-sm-3">
                        <div className={classes.courseLogo}>
                            {selectedCourse?.logo && (
                                <img
                                    src={selectedCourse.logo}
                                    alt="course logo"
                                />
                            )}
                        </div>
                    </div>
                    <div className="col-sm-5">
                        <div
                            className="d-flex flex-column justify-content-between"
                            style={{ height: '100%' }}
                        >
                            <div>
                                <p className={classes.name}>
                                    {selectedCourse?.courseTitle}
                                </p>
                                {/* <p className={classes.department}>
                                {selectedCourse?.department}
                            </p> */}
                                <p className={classes.description}>
                                    {selectedCourse?.description}
                                </p>
                            </div>
                            <div className="d-flex align-items-center">
                                <UserIcon />
                                <span className={classes.subscribers}>
                                    {selectedCourse?.enrolledBeneficiary}{' '}
                                    Subscribers
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className="col-sm-4">
                        <div
                            className={classNames(
                                classes.progressChart,
                                'd-flex justify-content-center align-items-center'
                            )}
                        >
                            <Chart
                                options={chartOptions}
                                series={
                                    selectedCourse
                                        ? [
                                              selectedCourse.ongoingPercentage,
                                              selectedCourse.completedPercentage,
                                          ]
                                        : []
                                }
                                type="pie"
                            />
                            <div>
                                <div className="d-flex align-items-center">
                                    <div
                                        className={classes.indicator}
                                        style={{
                                            background: chartIndicatorColors[0],
                                        }}
                                    />
                                    <span>Ongoing</span>
                                </div>
                                <div className="d-flex align-items-center">
                                    <div
                                        className={classes.indicator}
                                        style={{
                                            background: chartIndicatorColors[1],
                                        }}
                                    />
                                    <span>Completed</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    const renderStudentListTable = () => {
        return (
            <>
                <table className="table table-borderless">
                    <thead>
                        <tr>
                            <th scope="col">NAME</th>
                            <th scope="col">%COMPLETION</th>
                            <th scope="col">DATE ENROLLED</th>
                        </tr>
                    </thead>
                    <tbody>
                        {courseBeneficiariesData?.data?.map(beneficiary => (
                            <tr>
                                <td>
                                    <div className="d-flex align-items-center">
                                        <div className={classes.studentAvatar}>
                                            {beneficiary.profilePicture ? (
                                                <img
                                                    src={
                                                        beneficiary.profilePicture
                                                    }
                                                    alt="student avatar"
                                                />
                                            ) : (
                                                `${beneficiary?.beneficiaryName?.split(
                                                    ' '
                                                )?.[0]?.[0] ||
                                                    ''} ${beneficiary?.beneficiaryName?.split(
                                                    ' '
                                                )?.[1]?.[0] || ''}`
                                            )}
                                        </div>
                                        <span className={classes.text}>
                                            {beneficiary.beneficiaryName}
                                        </span>
                                    </div>
                                </td>
                                <td>
                                    <div className="d-flex align-items-center">
                                        <div
                                            className={classNames(
                                                'progress',
                                                classes.progress
                                            )}
                                        >
                                            <div
                                                className="progress-bar"
                                                role="progressbar"
                                                style={{
                                                    width:
                                                        beneficiary.percentageCompleted,
                                                }}
                                                aria-valuenow={
                                                    beneficiary.percentageCompleted
                                                }
                                                aria-valuemin="0"
                                                aria-valuemax="100"
                                            />
                                        </div>
                                        <span className={classes.text}>
                                            {beneficiary.percentageCompleted?.toFixed(
                                                2
                                            )}
                                            %
                                        </span>
                                    </div>
                                </td>
                                <td className={classes.text}>
                                    {moment(beneficiary.dateEnrolled).format(
                                        'DD MMM, YYYY'
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <div className="d-flex justify-content-end">
                    <PageChanger
                        pageCount={
                            courseBeneficiariesData?.paging?.pageCount || 0
                        }
                        onPageChange={currentPage =>
                            setBeneficiarySearchParams(prevState => ({
                                ...prevState,
                                currentPage,
                            }))
                        }
                        currentPage={beneficiarySearchParams.currentPage || 0}
                    />
                </div>
            </>
        )
    }

    const renderCourseStudentsList = () => {
        return (
            <div className={classes.studentsList}>
                <div
                    className={classNames(
                        'd-flex justify-content-end',
                        classes.exportCSVButton
                    )}
                >
                    {/* <Button variant="secondaryOutline">Export CSV</Button> */}
                </div>
                <div
                    className={classNames(
                        'row d-flex justify-content-between align-items-center',
                        classes.tableFilter
                    )}
                >
                    <div className="col-sm-5">
                        <HeaderSearch
                            placeholder="Search"
                            value={searchText.beneficiaryCourse}
                            onChange={beneficiaryCourse =>
                                setSearchText(prevState => ({
                                    ...prevState,
                                    beneficiaryCourse,
                                }))
                            }
                            onTriggerSearch={() =>
                                setBeneficiarySearchParams(prevState => ({
                                    ...prevState,
                                    searchText: searchText.beneficiaryCourse,
                                }))
                            }
                        />
                    </div>
                    <span style={{ width: 'auto' }}>Department: None</span>
                </div>
                {isFetchingCourseBeneficiaries ? (
                    <Spinner />
                ) : (
                    <div className="table-responsive">
                        {renderStudentListTable()}
                    </div>
                )}
            </div>
        )
    }

    const renderInformationPanel = () => {
        return (
            <div className={classes.informationPanel}>
                {selectedCourse ? (
                    <>
                        {renderCourseDetails()}
                        {renderCourseStudentsList()}
                    </>
                ) : (
                    <Empty
                        icon={EmptyCourseIcon}
                        text="No Course has been selected"
                    />
                )}
            </div>
        )
    }

    const renderCourse = course => {
        return (
            <div
                className={classNames(
                    classes.courseListItem,
                    selectedCourse?.courseTitle === course.courseTitle &&
                        classes.active
                )}
                onClick={() => setSelectedCourse(course)}
            >
                <p className={classes.name}>{course.courseTitle}</p>
                <p className={classes.department}>{course.description}</p>
            </div>
        )
    }

    const renderCoursesSearchPanel = () => {
        return (
            <div className={classes.coursesSearchPanel}>
                <div className={classes.addRightPadding}>
                    <HeaderSearch
                        placeholder="Search"
                        value={searchText.course}
                        onChange={course =>
                            setSearchText(prevState => ({
                                ...prevState,
                                course,
                            }))
                        }
                        onTriggerSearch={() =>
                            setCourseSearchText(searchText.course)
                        }
                    />
                </div>
                {isFetchingCourses ? (
                    <Spinner />
                ) : (
                    <div
                        className={classNames(
                            classes.addRightPadding,
                            classes.courseList
                        )}
                    >
                        {coursesData?.map((course, index) => (
                            <React.Fragment key={index}>
                                {renderCourse(course)}
                            </React.Fragment>
                        ))}
                    </div>
                )}
            </div>
        )
    }

    return (
        <div
            className={classNames(generalClasses.container, classes.container)}
        >
            <div>
                <p className={classes.title}>Courses</p>
                <div className="row">
                    <div className="col-12 col-lg-4">
                        {renderCoursesSearchPanel()}
                    </div>
                    <div className="col-12 col-lg-8">
                        {renderInformationPanel()}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default Courses
